import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio, Checkbox } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useH302Query } from './hooks/useH302Query';
import type { ReportRow } from '../../services/h302Api';
import './H302Form.css';

const CATEGORY_LABELS: Record<string, string> = {
  '1': '貨款', '2': '直接單位-直接費用', '3': '直接單位-間接費用',
  '4': '間接單位-間接費用', '5': '營業外支出', '6': '採購佣金-直接', '7': '銷售佣金-直接',
};

const H302Form: React.FC = () => {
  const { loading, data, summaries, queried, noData, runQuery, reset } = useH302Query();

  const [reportType, setReportType] = useState<string>('vendor');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState('');
  const [invoiceDateTo, setInvoiceDateTo] = useState('');
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [paymentDateFrom, setPaymentDateFrom] = useState('');
  const [paymentDateTo, setPaymentDateTo] = useState('');
  const [invoiceNoFrom, setInvoiceNoFrom] = useState('');
  const [invoiceNoTo, setInvoiceNoTo] = useState('');
  const [balanceFilter, setBalanceFilter] = useState<string>('balance_only');
  const [selectedCats, setSelectedCats] = useState<string[]>(['1','2','3','4','5','6','7']);

  const handleQuery = () => {
    const cats = selectedCats.length === 7 ? '' : selectedCats.join(',');
    runQuery({
      report_type: reportType,
      invoice_date_from: invoiceDateFrom.trim() || undefined,
      invoice_date_to: invoiceDateTo.trim() || undefined,
      vendor_from: vendorFrom.trim() || undefined,
      vendor_to: vendorTo.trim() || undefined,
      dept_from: deptFrom.trim() || undefined,
      dept_to: deptTo.trim() || undefined,
      payment_date_from: paymentDateFrom.trim() || undefined,
      payment_date_to: paymentDateTo.trim() || undefined,
      invoice_no_from: invoiceNoFrom.trim() || undefined,
      invoice_no_to: invoiceNoTo.trim() || undefined,
      balance_filter: balanceFilter,
      categories: cats || undefined,
    });
  };

  const handleReset = () => {
    setReportType('vendor');
    setInvoiceDateFrom(''); setInvoiceDateTo('');
    setVendorFrom(''); setVendorTo('');
    setDeptFrom(''); setDeptTo('');
    setPaymentDateFrom(''); setPaymentDateTo('');
    setInvoiceNoFrom(''); setInvoiceNoTo('');
    setBalanceFilter('balance_only');
    setSelectedCats(['1','2','3','4','5','6','7']);
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const title = reportType === 'dept' ? '應付帳款明細表（部門別）' : '應付帳款明細表（廠商別）';
    const win = window.open('', '_blank');
    if (!win) return;
    const isVendor = reportType !== 'dept';
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}
table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}
th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr>
<th>${isVendor ? '廠商' : '部門'}</th><th>${isVendor ? '部門' : '廠商'}</th>
<th>請款單號</th><th>請款日期</th><th>應付金額</th>
<th>沖帳單號</th><th>沖帳金額</th><th>應付餘額</th><th>付款條件</th>
</tr></thead><tbody>
${data.map(r => `<tr>
<td>${isVendor ? (r.vendor_name || r.FH08_01) : (r.dept_name || r.FH110_37)}</td>
<td>${isVendor ? (r.dept_name || r.FH110_37) : (r.vendor_name || r.FH08_01)}</td>
<td>${r.FH08_02}</td><td>${r.FH110_11}</td><td class="right">${r.FH08_11?.toFixed(2)}</td>
<td>${r.FH081_03}</td><td class="right">${r.FH081_11 ? r.FH081_11.toFixed(2) : ''}</td>
<td class="right">${r.FH081_99?.toFixed(2)}</td><td>${r.FH110_PAYC || ''}</td>
</tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const columns: ColumnsType<ReportRow> = reportType === 'dept'
    ? [
        { title: '部門', key: 'dept', width: 100, render: (_: unknown, r: ReportRow) => r.dept_name || r.FH110_37 },
        { title: '廠商', key: 'vendor', width: 120, render: (_: unknown, r: ReportRow) => r.vendor_name || r.FH08_01 },
        { title: '請款單號', dataIndex: 'FH08_02', key: 'FH08_02', width: 110 },
        { title: '請款日期', dataIndex: 'FH110_11', key: 'FH110_11', width: 100 },
        { title: '應付金額', dataIndex: 'FH08_11', key: 'FH08_11', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
        { title: '沖帳單號', dataIndex: 'FH081_03', key: 'FH081_03', width: 100 },
        { title: '沖帳金額', dataIndex: 'FH081_11', key: 'FH081_11', width: 100, align: 'right' as const, render: (v: number) => v ? v.toFixed(2) : '' },
        { title: '應付餘額', dataIndex: 'FH081_99', key: 'FH081_99', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
        { title: '付款條件', dataIndex: 'FH110_PAYC', key: 'FH110_PAYC', ellipsis: true, width: 140 },
      ]
    : [
        { title: '廠商', key: 'vendor', width: 120, render: (_: unknown, r: ReportRow) => r.vendor_name || r.FH08_01 },
        { title: '部門', key: 'dept', width: 100, render: (_: unknown, r: ReportRow) => r.dept_name || r.FH110_37 },
        { title: '請款單號', dataIndex: 'FH08_02', key: 'FH08_02', width: 110 },
        { title: '請款日期', dataIndex: 'FH110_11', key: 'FH110_11', width: 100 },
        { title: '應付金額', dataIndex: 'FH08_11', key: 'FH08_11', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
        { title: '沖帳單號', dataIndex: 'FH081_03', key: 'FH081_03', width: 100 },
        { title: '沖帳金額', dataIndex: 'FH081_11', key: 'FH081_11', width: 100, align: 'right' as const, render: (v: number) => v ? v.toFixed(2) : '' },
        { title: '應付餘額', dataIndex: 'FH081_99', key: 'FH081_99', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
        { title: '付款條件', dataIndex: 'FH110_PAYC', key: 'FH110_PAYC', ellipsis: true, width: 140 },
      ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.H302} action="query">
      <div className="h302-container">
        <div className="h302-header">
          <h2>應付帳款明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="vendor">廠商別</Radio.Button>
              <Radio.Button value="dept">部門別</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('廠商代號起')}
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('廠商代號迄')}
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('請款日期起 (YYYY-MM-DD)')}
              <Input value={invoiceDateFrom} onChange={e => setInvoiceDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('請款日期迄 (YYYY-MM-DD)')}
              <Input value={invoiceDateTo} onChange={e => setInvoiceDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號起')}
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('部門代號迄')}
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('沖帳日期起 (YYYY-MM-DD)')}
              <Input value={paymentDateFrom} onChange={e => setPaymentDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('沖帳日期迄 (YYYY-MM-DD)')}
              <Input value={paymentDateTo} onChange={e => setPaymentDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('請款單號起')}
              <Input value={invoiceNoFrom} onChange={e => setInvoiceNoFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('請款單號迄')}
              <Input value={invoiceNoTo} onChange={e => setInvoiceNoTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
          </Space>
          <div style={{ marginTop: 12 }}>
            {filterLabel('餘額篩選')}
            <Radio.Group value={balanceFilter} onChange={e => setBalanceFilter(e.target.value)}>
              <Radio value="all">全部列出</Radio>
              <Radio value="balance_only">只列有餘額</Radio>
            </Radio.Group>
          </div>
          <div style={{ marginTop: 12 }}>
            {filterLabel('請款類別')}
            <Checkbox
              checked={selectedCats.length === 7}
              indeterminate={selectedCats.length > 0 && selectedCats.length < 7}
              onChange={e => setSelectedCats(e.target.checked ? ['1','2','3','4','5','6','7'] : [])}
            >全部</Checkbox>
            <Checkbox.Group
              value={selectedCats}
              onChange={v => setSelectedCats(v as string[])}
              options={Object.entries(CATEGORY_LABELS).map(([k, v]) => ({ label: v, value: k }))}
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
              <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
            </Space>
          </div>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {summaries.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                {reportType === 'dept' ? '部門' : '廠商'}筆數：
                {summaries.map(s => `${s.name || s.code}(${s.count})`).join('、')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FH08_01}-${r.FH08_02}-${r.FH081_03}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1100 }}
              style={{ marginBottom: 16 }}
            />
            {summaries.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>{reportType === 'dept' ? '部門' : '廠商'}合計</div>
                <Table
                  columns={[
                    { title: '代碼', dataIndex: 'code', key: 'code', width: 100 },
                    { title: '名稱', dataIndex: 'name', key: 'name', width: 150 },
                    { title: '筆數', dataIndex: 'count', key: 'count', width: 80, align: 'right' as const },
                    { title: '應付合計', dataIndex: 'total_payable', key: 'total_payable', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
                    { title: '已付合計', dataIndex: 'total_paid', key: 'total_paid', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
                    { title: '餘額合計', dataIndex: 'total_balance', key: 'total_balance', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
                  ]}
                  dataSource={summaries}
                  rowKey="code"
                  size="small"
                  pagination={false}
                  scroll={{ x: 700 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default H302Form;
